/**
 * SimCheckChatWidget - New AI Chat Widget using assistant-ui
 *
 * Replaces the old ChatWidget with assistant-ui components for:
 * - Streaming responses
 * - Markdown rendering
 * - Better UX with maintained library
 *
 * Default chat widget. VITE_USE_NEW_CHAT=false falls back to the legacy Gemini ChatWidget.
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X, Minus, Send, Loader2, Trash2, CheckCircle2, XCircle, ChevronRight, ChevronDown, Wrench, BookOpen, Info } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useSimCheckChat, ChatMessage, ChatAction, ThinkingEvent } from '../../hooks/useSimCheckChat';

function MarkdownContent({ text }: { text: string }) {
  return (
    <div className="break-words text-sm leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h3 className="font-semibold text-sm mt-3 mb-1.5">{children}</h3>,
          h2: ({ children }) => <h3 className="font-semibold text-sm mt-3 mb-1.5">{children}</h3>,
          h3: ({ children }) => <h4 className="font-semibold text-sm mt-2.5 mb-1">{children}</h4>,
          h4: ({ children }) => <h4 className="font-semibold text-sm mt-2 mb-1">{children}</h4>,
          p: ({ children }) => <p className="my-1.5">{children}</p>,
          ul: ({ children }) => <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:opacity-80">
              {children}
            </a>
          ),
          code: ({ className, children }) => {
            const isBlock = className?.includes('language-');
            if (isBlock) {
              return <code className={cn('block', className)}>{children}</code>;
            }
            return <code className="px-1 py-0.5 rounded bg-background text-xs font-mono">{children}</code>;
          },
          pre: ({ children }) => (
            <pre className="my-2 p-2 rounded bg-background text-xs font-mono overflow-x-auto">{children}</pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-1.5 pl-3 border-l-2 border-border text-muted-foreground">{children}</blockquote>
          ),
          hr: () => <hr className="my-3 border-border" />,
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto">
              <table className="min-w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-background/50">{children}</thead>,
          th: ({ children }) => (
            <th className="border border-border px-2 py-1 text-left font-semibold">{children}</th>
          ),
          td: ({ children }) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function formatToolName(name: string): string {
  // mcp__simcheck__list_flows → list_flows
  const stripped = name.replace(/^mcp__[^_]+__/, '');
  return stripped || name;
}

function ThinkingEventRow({ event }: { event: ThinkingEvent }) {
  if (event.kind === 'tool_call') {
    const isPending = event.status === 'pending';
    return (
      <div className="flex items-start gap-2 text-xs py-1">
        <Wrench className={cn('h-3.5 w-3.5 mt-0.5 flex-shrink-0', isPending ? 'text-muted-foreground animate-pulse' : 'text-foreground')} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-medium truncate">{formatToolName(event.toolName)}</span>
            {isPending ? (
              <span className="text-muted-foreground">
                {typeof event.elapsed === 'number' ? `${event.elapsed.toFixed(0)}s…` : 'running…'}
              </span>
            ) : (
              <CheckCircle2 className="h-3 w-3 text-green-600" />
            )}
          </div>
          {!!(event.input && typeof event.input === 'object' && Object.keys(event.input as object).length > 0) && (
            <pre className="mt-0.5 text-[10px] font-mono text-muted-foreground whitespace-pre-wrap break-all line-clamp-3">
              {JSON.stringify(event.input, null, 2)}
            </pre>
          )}
        </div>
      </div>
    );
  }
  if (event.kind === 'skill_read') {
    const short = event.file.split('/').slice(-2).join('/');
    return (
      <div className="flex items-start gap-2 text-xs py-1">
        <BookOpen className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-foreground" />
        <div className="min-w-0 flex-1">
          <span className="font-mono truncate">Read {short}</span>
        </div>
      </div>
    );
  }
  // system
  return (
    <div className="flex items-start gap-2 text-xs py-1">
      <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <span className="text-muted-foreground">
          {event.subtype === 'compacted'
            ? `Context compacted (${(event.details?.preTokens as number | undefined) ?? '?'} → ${(event.details?.postTokens as number | undefined) ?? '?'} tokens)`
            : event.subtype}
        </span>
      </div>
    </div>
  );
}

function ThinkingTrail({
  events,
  isActive,
  defaultExpanded = false,
}: {
  events: ThinkingEvent[];
  isActive: boolean;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const count = events.length;
  const pendingTool = events.find(e => e.kind === 'tool_call' && e.status === 'pending') as
    | Extract<ThinkingEvent, { kind: 'tool_call' }>
    | undefined;

  // Live preview when active: show the latest event one-liner next to the disclosure.
  const livePreview = (() => {
    if (!isActive) return null;
    if (pendingTool) return `Calling ${formatToolName(pendingTool.toolName)}`;
    const latest = events[events.length - 1];
    if (!latest) return 'Thinking';
    if (latest.kind === 'skill_read') return `Reading ${latest.file.split('/').slice(-1)[0]}`;
    if (latest.kind === 'tool_call') return `${formatToolName(latest.toolName)} done`;
    return 'Thinking';
  })();

  return (
    <div className={cn('mb-2', count > 0 || isActive ? 'block' : 'hidden')}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className={cn(
          'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md',
          'text-muted-foreground hover:text-foreground hover:bg-background/60',
          'transition-colors w-full text-left'
        )}
      >
        {expanded ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />}
        {isActive && <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />}
        <span className="font-medium">
          {isActive ? 'Thinking' : 'Thought'}
          {count > 0 && <span className="text-muted-foreground/70"> · {count} {count === 1 ? 'step' : 'steps'}</span>}
        </span>
        {!expanded && livePreview && (
          <span className="text-muted-foreground/70 truncate">— {livePreview}</span>
        )}
      </button>
      {expanded && count > 0 && (
        <div className="mt-1 ml-1 pl-3 border-l border-border/60 space-y-0">
          {events.map(e => <ThinkingEventRow key={e.id} event={e} />)}
        </div>
      )}
    </div>
  );
}

function ActionButtons({ actions, onAction }: { actions: ChatAction[]; onAction: (action: ChatAction) => void }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {actions.map(action => (
        <Button
          key={action.id}
          size="sm"
          variant={action.type === 'approve' ? 'default' : action.type === 'reject' ? 'outline' : 'secondary'}
          className={cn(
            'h-8 text-xs',
            action.type === 'approve' && 'bg-green-600 hover:bg-green-700 text-white',
            action.type === 'reject' && 'border-destructive text-destructive hover:bg-destructive/10',
          )}
          onClick={() => onAction(action)}
        >
          {action.type === 'approve' && <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
          {action.type === 'reject' && <XCircle className="h-3.5 w-3.5 mr-1.5" />}
          {action.label}
        </Button>
      ))}
    </div>
  );
}

function MessageBubble({ message, onAction, isStreamingThis }: { message: ChatMessage; onAction: (action: ChatAction) => void; isStreamingThis: boolean }) {
  const isUser = message.role === 'user';
  const hasContent = !!message.content;
  const events = message.thinkingEvents ?? [];
  const showTrail = !isUser && (events.length > 0 || (isStreamingThis && !hasContent));

  return (
    <div className={cn('flex gap-3 px-4 py-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar */}
      <div className={cn(
        'flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium',
        isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
      )}>
        {isUser ? 'You' : <Bot className="h-4 w-4" />}
      </div>

      {/* Content */}
      <div className={cn(
        'min-w-0 rounded-lg px-4 py-2 text-sm',
        isUser
          ? 'max-w-[80%] bg-primary text-primary-foreground'
          // Full width beside the avatar so tables have room; min-w-0 lets a
          // wider table scroll inside the bubble instead of being cut off.
          : 'flex-1 bg-muted text-foreground'
      )}>
        {showTrail && (
          <ThinkingTrail events={events} isActive={isStreamingThis && !hasContent} />
        )}
        {hasContent ? (
          isUser
            ? <div className="whitespace-pre-wrap break-words">{message.content}</div>
            : <MarkdownContent text={message.content} />
        ) : (
          !showTrail && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Thinking...</span>
            </div>
          )
        )}
        {message.pendingApproval && (
          <div className={cn('mt-2 rounded-md border px-3 py-2 text-xs', message.pendingApproval.decision === 'approve' ? 'border-green-600/40 bg-green-600/5' : message.pendingApproval.decision === 'reject' ? 'border-destructive/40 bg-destructive/5' : message.pendingApproval.decision === 'expired' ? 'border-border bg-background/50' : 'border-amber-500/40 bg-amber-500/5')}>
            <div className="font-medium mb-1">
              {message.pendingApproval.decision === 'approve' ? 'Approved'
                : message.pendingApproval.decision === 'reject' ? 'Rejected'
                : message.pendingApproval.decision === 'expired' ? 'Not answered in time, nothing ran'
                : 'Waiting for your approval'}
              <span className="text-muted-foreground font-normal"> · {formatToolName(message.pendingApproval.tool)}</span>
            </div>
            <ul className="space-y-0.5 text-muted-foreground">
              {message.pendingApproval.summary.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          </div>
        )}
        {message.actions && message.actions.length > 0 && (
          <ActionButtons actions={message.actions} onAction={onAction} />
        )}
      </div>
    </div>
  );
}

function ChatPanel({
  chat,
  onClose,
  onMinimize,
}: {
  chat: ReturnType<typeof useSimCheckChat>;
  onClose: () => void;
  onMinimize: () => void;
}) {
  const { messages, isStreaming, isHydrating, error, sendMessage, clearMessages, decideApproval } = chat;
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isStreaming || isHydrating) return;
    const msg = input;
    setInput('');
    await sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={cn(
      'w-[520px] max-w-[calc(100vw-3rem)] h-[640px] max-h-[calc(100vh-6rem)] flex flex-col rounded-xl overflow-hidden',
      'bg-card border border-border shadow-2xl',
      'animate-in slide-in-from-bottom-5 duration-300'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <span className="font-semibold text-foreground">SimCheck AI</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={clearMessages} title="New conversation">
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMinimize}>
            <Minus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 && isHydrating ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            Loading your last conversation…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <Bot className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-foreground mb-1">SimCheck AI Assistant</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Ask me about your test flows, locations, schedules, or help debugging test failures.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['Show my test flows', 'List locations', 'Analyze recent failures'].map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => sendMessage(suggestion)}
                  className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => {
              const isLast = idx === messages.length - 1;
              const isStreamingThis = isStreaming && isLast && msg.role === 'assistant';
              return (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isStreamingThis={isStreamingThis}
                  onAction={(action) => {
                    const approvalId = typeof action.payload?.approvalId === 'string' ? (action.payload.approvalId as string) : null;
                    // A parked tool call: the decision must reach the server's
                    // approval doc. A chat reply never unblocks it.
                    if (approvalId && (action.type === 'approve' || action.type === 'reject')) {
                      void decideApproval(approvalId, action.type);
                    } else if (action.type === 'approve') {
                      sendMessage(`Yes, I approve this action.`);
                    } else if (action.type === 'reject') {
                      sendMessage(`No, cancel this action.`);
                    } else {
                      sendMessage(action.label);
                    }
                  }}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-destructive/10 text-destructive text-xs">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-3">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your tests..."
            className={cn(
              'flex-1 resize-none rounded-lg px-3 py-2 text-sm',
              'bg-background border border-border',
              'focus:outline-none focus:ring-2 focus:ring-primary/50',
              'placeholder:text-muted-foreground',
              'max-h-32'
            )}
            rows={1}
            disabled={isStreaming || isHydrating}
          />
          <Button
            onClick={handleSend}
            size="icon"
            className="h-9 w-9 rounded-lg"
            disabled={!input.trim() || isStreaming || isHydrating}
          >
            {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SimCheckChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // The conversation lives here, not in the panel, so minimizing or closing
  // the panel keeps it. History loads the first time the chat is opened.
  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => { if (isOpen) setEverOpened(true); }, [isOpen]);
  const chat = useSimCheckChat(everOpened);

  // Keyboard shortcut: Ctrl+Shift+A to toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setIsMinimized(false);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return createPortal(
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-4">
      {/* Chat Panel */}
      {isOpen && !isMinimized && (
        <ChatPanel
          chat={chat}
          onClose={() => setIsOpen(false)}
          onMinimize={() => setIsMinimized(true)}
        />
      )}

      {/* Minimized Bar */}
      {isOpen && isMinimized && (
        <div
          onClick={() => setIsMinimized(false)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-full',
            'bg-card border border-border shadow-lg cursor-pointer',
            'hover:bg-accent transition-colors'
          )}
        >
          <Bot className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">SimCheck AI</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-2"
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Floating Button */}
      {!isOpen && (
        <Button
          onClick={() => { setIsOpen(true); setIsMinimized(false); }}
          size="icon"
          className={cn(
            'h-14 w-14 rounded-full shadow-lg',
            'bg-primary hover:bg-primary/90',
            'transition-transform hover:scale-105'
          )}
          title="SimCheck AI (Ctrl+Shift+A)"
        >
          <Bot className="h-6 w-6" />
        </Button>
      )}
    </div>,
    document.body
  );
}
